/**
 * ManagerGuide — Step-by-step guided workflow for managers
 *
 * Four-phase stepper: Plan → Transfers → Production → Review
 * Each step shows a focused view with clear action buttons
 * that link to existing functionality (no new backend changes).
 */
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ClipboardList,
  Play,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Activity,
  Package,
  TrendingUp,
  RefreshCw,
  ChevronRight,
  Clock,
  Scissors,
  Box,
  AlertCircle,
  Plus,
  ExternalLink,
  ArrowLeftRight,
  Search,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  Calendar,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import productionService from '../../services/productionService';
import inventoryService, { PendingApprovalLot } from '../../services/inventoryService';
import { productionOutputScanService } from '../../services/productionOutputScanService';
import workScheduleService, { ScheduledWorkforceResponse } from '../../services/workScheduleService';

import { useLanguage } from '../../i18n/LanguageContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { wsService } from '../../services/websocket';
import { getStatusBadgeClass } from '../../utils/statusColors';
/** Format YYYY-MM-DD → MM/DD/YY */
const fmtDate = (d: string) => { const [y, m, dd] = d.split('-'); return `${m}/${dd}/${y.slice(2)}`; };
import DatePickerDropdown from '../../components/DatePickerDropdown';
import ProductionProgressTable from '../../components/ProductionProgressTable';
import ManagerApprovalQueue, { ManagerApprovalQueueHandle } from '../Supervisor/ManagerApprovalQueue';
import AddSessionModal from '../Supervisor/components/AddSessionModal';
import CalendarView from '../Supervisor/components/CalendarView';

import IcsTransferDetailView from '../Supervisor/components/IcsTransferDetailView';
import CreateTransferView from '../Supervisor/components/CreateTransferView';
import DailyTransferOutView from '../Supervisor/components/DailyTransferOutView';
import SessionDetailsModal, { getDefaultSessionTab } from '../Supervisor/components/SessionDetailsModal';
import CancelSessionModal from '../../components/CancelSessionModal';
import { isSessionCancellable } from '../../utils/sessionStatus';
import { isSessionEditable } from '../../utils/isSessionEditable';
import { useAuth } from '../../hooks/useAuth';

type Step = 'plan' | 'transfers' | 'produce' | 'review';
const VALID_STEPS: Step[] = ['plan', 'transfers', 'produce', 'review'];

const getInitialStep = (): Step => {
  const hash = window.location.hash.replace('#', '');
  return VALID_STEPS.includes(hash as Step) ? (hash as Step) : 'plan';
};

const getToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const shiftDate = (dateStr: string, days: number): string => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDateLabel = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${weekday}, ${mm}/${dd}/${d.getFullYear()}`;
};

// ── Main Component ────────────────────────────────────────

const ManagerGuide: React.FC = () => {
  const { t } = useLanguage();
  usePageTitle(t('managerGuide.title'));
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState<Step>(getInitialStep);
  const [hideKpi, setHideKpi] = useState(false);
  const [guideDate, setGuideDate] = useState(getToday());

  // Sync step to URL hash so it persists on refresh
  useEffect(() => {
    window.location.hash = activeStep;
  }, [activeStep]);

  useEffect(() => {
    wsService.connect();
    const refreshProduction = () => {
      queryClient.invalidateQueries(['productionSessions']);
      queryClient.invalidateQueries(['production-progress']);
    };
    const refreshTransfers = () => {
      queryClient.invalidateQueries(['pending-approvals']);
      queryClient.invalidateQueries(['recent-transfers-hte']);
    };
    wsService.on('production-session-updated', refreshProduction);
    wsService.on('production-session-created', refreshProduction);
    wsService.on('station-updated', refreshProduction);
    wsService.on('box-packed', refreshProduction);
    wsService.on('box-cancelled', refreshProduction);
    wsService.on('box-deleted', refreshProduction);
    wsService.on('pending-label-voided', refreshProduction);
    wsService.on('hte-transfer-updated', refreshTransfers);
    wsService.on('hte-transfer-created', refreshTransfers);
    return () => {
      wsService.off('production-session-updated', refreshProduction);
      wsService.off('production-session-created', refreshProduction);
      wsService.off('station-updated', refreshProduction);
      wsService.off('box-packed', refreshProduction);
      wsService.off('box-cancelled', refreshProduction);
      wsService.off('box-deleted', refreshProduction);
      wsService.off('pending-label-voided', refreshProduction);
      wsService.off('hte-transfer-updated', refreshTransfers);
      wsService.off('hte-transfer-created', refreshTransfers);
    };
  }, [queryClient]);

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-6rem)]">
      {/* Stepper */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-3 shrink-0">
        <div className="flex items-center justify-between">
          {(['plan', 'transfers', 'produce', 'review'] as Step[]).map((step, idx) => {
            const isActive = step === activeStep;
            const stepIdx = ['plan', 'transfers', 'produce', 'review'].indexOf(activeStep);
            const isPast = idx < stepIdx;
            const labels = ['managerGuide.steps.plan', 'managerGuide.steps.transfers', 'managerGuide.steps.production', 'managerGuide.steps.review'];
            return (
              <React.Fragment key={step}>
                {idx > 0 && <div className={`flex-1 h-0.5 mx-3 rounded ${isPast ? 'bg-indigo-500' : 'bg-gray-200 dark:bg-gray-600'}`} />}
                <button
                  onClick={() => {
                    if (step === activeStep) { setHideKpi(h => !h); }
                    else { setActiveStep(step); setHideKpi(false); }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isActive ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-semibold ring-2 ring-indigo-500/30'
                    : isPast ? 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                    : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <span className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
                    isActive ? 'bg-indigo-500 text-white'
                    : isPast ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                  }`}>
                    {isPast ? <CheckCircle2 className="w-3.5 h-3.5" /> : idx + 1}
                  </span>
                  <span className="hidden sm:inline">{t(labels[idx])}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {activeStep === 'plan' && <PlanStep t={t} onNext={() => setActiveStep('transfers')} hideKpi={hideKpi} guideDate={guideDate} setGuideDate={setGuideDate} />}
      {activeStep === 'transfers' && <TransfersStep t={t} onPrev={() => setActiveStep('plan')} onNext={() => setActiveStep('produce')} hideKpi={hideKpi} guideDate={guideDate} setGuideDate={setGuideDate} />}
      {activeStep === 'produce' && <ProduceStep t={t} onPrev={() => setActiveStep('transfers')} onNext={() => setActiveStep('review')} hideKpi={hideKpi} guideDate={guideDate} setGuideDate={setGuideDate} />}
      {activeStep === 'review' && <ReviewStep t={t} onPrev={() => setActiveStep('produce')} onComplete={() => setActiveStep('plan')} hideKpi={hideKpi} guideDate={guideDate} setGuideDate={setGuideDate} />}
    </div>
  );
};

// ── KPI Card ──────────────────────────────────────────────

function KpiCard({ icon, label, value, sublabel, bg }: {
  icon: React.ReactNode; label: string; value: string | number; sublabel?: string; bg: string;
}) {
  return (
    <div className={`rounded-lg p-3 ${bg} border border-gray-200 dark:border-gray-700`}>
      <div className="flex items-center gap-2 mb-0.5">{icon}<span className="text-xs text-gray-500 dark:text-gray-400">{label}</span></div>
      <div className="text-xl font-bold text-gray-900 dark:text-white">{value}</div>
      {sublabel && <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{sublabel}</div>}
    </div>
  );
}

// ── Date Navigator (reusable inline component) ───────────

type SetGuideDate = React.Dispatch<React.SetStateAction<string>>;

function DateNav({ guideDate, setGuideDate, t }: { guideDate: string; setGuideDate: SetGuideDate; t: (key: string) => string }) {
  const isToday = guideDate === getToday();
  return (
    <div className="inline-flex items-center gap-1">
      {!isToday && (
        <button onClick={() => setGuideDate(getToday())} className="px-2 py-0.5 text-[10px] font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors">
          {t('dailyWorkQueue.today')}
        </button>
      )}
      <button type="button" onClick={() => setGuideDate(d => shiftDate(d, -1))} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <DatePickerDropdown selected={guideDate} onSelect={(d) => setGuideDate(d)}>
        {({ onClick }) => (
          <button type="button" onClick={onClick} className="text-xs font-medium text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer px-0.5 whitespace-nowrap">
            {formatDateLabel(guideDate)}
          </button>
        )}
      </DatePickerDropdown>
      <button type="button" onClick={() => setGuideDate(d => shiftDate(d, 1))} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── STEP 1: Plan ──────────────────────────────────────────

function PlanStep({ t, onNext, hideKpi, guideDate, setGuideDate }: { t: (key: string) => string; onNext: () => void; hideKpi: boolean; guideDate: string; setGuideDate: SetGuideDate }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [showAddSession, setShowAddSession] = useState(false);
  const [addSessionDate, setAddSessionDate] = useState<string | undefined>(undefined);
  const [editingSession, setEditingSession] = useState<any | null>(null);
  const [selectedSession, setSelectedSession] = useState<any | null>(null);
  const [deleteConfirmSession, setDeleteConfirmSession] = useState<any | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(guideDate);
  const td = guideDate;

  // Sync calendar selection when parent date changes
  useEffect(() => { setSelectedDate(guideDate); }, [guideDate]);

  const { data: sessions = [], isLoading } = useQuery(
    ['productionSessions', 'withSubSessions'],
    () => productionService.getProductionSessions({ include: 'sub_sessions' }),
    { staleTime: 30000, refetchInterval: 60000, refetchOnMount: 'always' },
  );
  const { data: workforce } = useQuery<ScheduledWorkforceResponse>(['scheduled-workforce', td], () => workScheduleService.getScheduledWorkforce(td), { staleTime: 30000 });

  // Filter sessions for the selected date
  const filteredSessions = useMemo(() => {
    if (!selectedDate) return sessions.filter((s: any) => s.scheduled_date?.split('T')[0] === td);
    return sessions.filter((s: any) => s.scheduled_date?.split('T')[0] === selectedDate);
  }, [sessions, selectedDate, td]);

  const stats = useMemo(() => {
    const todaySessions = sessions.filter((s: any) => s.scheduled_date?.split('T')[0] === td && s.status !== 'cancelled');
    const planned = todaySessions.filter((s: any) => ['pending', 'submitted', 'received', 'ready'].includes(s.status)).length;
    const active = todaySessions.filter((s: any) => ['active', 'in_progress', 'paused'].includes(s.status)).length;
    const completed = todaySessions.filter((s: any) => s.status === 'completed').length;

    return {
      total: planned + active + completed,
      planned,
      active,
      completed,
    };
  }, [sessions, td]);

  const handleDateSelect = useCallback((date: string) => {
    setSelectedDate(date);
    setGuideDate(date);
  }, [setGuideDate]);

  // no-op for session click from calendar (we just use the list)
  const handleSessionClick = useCallback((session: any) => {
    setSelectedSession(session);
  }, []);

  // Fetch full session data and open edit modal
  const handleEditClick = useCallback(async (session: any) => {
    try {
      const fullSession = await productionService.getProductionSession(session.session_number);
      setEditingSession(fullSession);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to load session for editing');
    }
  }, []);

  // Cancelling a session goes through the shared <CancelSessionModal> (WCO-2706) — it owns the
  // reason dropdown, the password confirmation and the mutation.

  return (
    <>
      {/* KPI Cards */}
      {!hideKpi && (() => {
        const isToday = td === getToday();
        const dateLabel = isToday ? t('managerGuide.plan.sessionsToday') : `Sessions ${fmtDate(td)}`;
        const schedLabel = isToday ? t('managerGuide.plan.scheduledToday') : `scheduled ${fmtDate(td)}`;
        return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            icon={<ClipboardList className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
            label={dateLabel}
            value={isLoading ? '—' : stats.total}
            sublabel={`${stats.planned} ${t('managerGuide.plan.planned')}, ${stats.active} ${t('managerGuide.plan.active')}, ${stats.completed} ${t('managerGuide.plan.completed')}`}
            bg="bg-indigo-50 dark:bg-indigo-900/20"
          />
          <KpiCard
            icon={<Scissors className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />}
            label={t('managerGuide.plan.operators')}
            value={workforce ? `${workforce.summary.available_operators}/${workforce.summary.total_operators}` : '—'}
            sublabel={schedLabel}
            bg="bg-cyan-50 dark:bg-cyan-900/20"
          />
          <KpiCard
            icon={<Box className="w-4 h-4 text-orange-600 dark:text-orange-400" />}
            label={t('managerGuide.plan.packers')}
            value={workforce ? `${workforce.summary.available_packers}/${workforce.summary.total_packers}` : '—'}
            sublabel={schedLabel}
            bg="bg-orange-50 dark:bg-orange-900/20"
          />
          <KpiCard
            icon={<Activity className="w-4 h-4 text-purple-600 dark:text-purple-400" />}
            label={t('managerGuide.plan.stations')}
            value={workforce?.summary?.total_stations ?? '—'}
            sublabel={schedLabel}
            bg="bg-purple-50 dark:bg-purple-900/20"
          />
        </div>
        );
      })()}

      {/* Two-column: Calendar + Session list */}
      <div className={`grid grid-cols-1 ${calendarExpanded ? '' : 'lg:grid-cols-2'} gap-4 flex-1`}>
        {/* Calendar */}
        <div className="flex flex-col relative">
          <CalendarView
            sessions={sessions}
            onSessionClick={handleSessionClick}
            onDateSelect={handleDateSelect}
            onDateDoubleClick={(date) => { setAddSessionDate(date); setShowAddSession(true); }}
            selectedDate={selectedDate}
            weekStartsOn={1}
            rangeOptions={[7, 14, 30]}
            defaultRange={14}
            className="h-full flex flex-col"
            expanded={calendarExpanded}
          />
          {/* Expand / Collapse toggle */}
          <button
            type="button"
            onClick={() => setCalendarExpanded(e => !e)}
            title={calendarExpanded ? 'Collapse calendar' : 'Expand calendar'}
            className="absolute bottom-2 right-2 p-1.5 rounded-lg bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors z-10"
          >
            {calendarExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>

        {/* Session list */}
        {!calendarExpanded && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {td === getToday() ? t('managerGuide.plan.todaysSessions') : `Sessions for ${fmtDate(td)}`}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setAddSessionDate(selectedDate || undefined); setShowAddSession(true); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                {t('managerGuide.plan.createSession')}
              </button>
              <Link to="/production-planning" className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline">
                <ExternalLink className="w-3 h-3" />
                {t('managerGuide.plan.viewAll')}
              </Link>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="flex flex-col justify-center items-center py-12 text-gray-400 gap-2">
              <ClipboardList className="w-8 h-8 opacity-50" />
              <p className="text-sm">{t('managerGuide.plan.noSessions')}</p>
              <button onClick={() => setShowAddSession(true)} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">
                {t('managerGuide.plan.createFirst')}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700 overflow-y-auto flex-1">
              {filteredSessions.filter((s: any) => s.status !== 'cancelled').map((session: any) => {
                const subs = (session.sub_sessions || []).filter((s: any) => s.status !== 'cancelled');
                const totalBoxes = subs.reduce((sum: number, s: any) => sum + (Number(s.boxes_produced) || 0), 0);
                const expectedBoxes = subs.reduce((sum: number, s: any) => sum + (Number(s.expected_boxes) || 0), 0);
                const pct = expectedBoxes > 0 ? Math.min(100, (totalBoxes / expectedBoxes) * 100) : 0;

                return (
                  <div key={session.session_number} onClick={() => setSelectedSession(session)} className="px-4 py-3 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(session.status)}`}>
                      {t(`status.${session.status}`) || session.status}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 font-mono">{session.session_number}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{session.product_name}</span>
                        {session.product_chinese_name && <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{session.product_chinese_name}</span>}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {subs.length} {subs.length === 1 ? 'station' : 'stations'}
                        {subs.length > 0 && ` · ${subs.map((s: any) => s.station_name).filter(Boolean).join(', ')}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 w-32 shrink-0">
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full">
                        <div className={`h-1.5 rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 w-12 text-right font-mono">{totalBoxes}/{expectedBoxes}</span>
                    </div>
                    {/* WCO-2706: running/finished sessions can no longer be cancelled */}
                    {isSessionCancellable(session.status) && (
                      <div className="flex items-center gap-0.5 shrink-0">
                        {isSessionEditable(session) && (
                          <button
                            type="button"
                            title="Edit session"
                            onClick={(e) => { e.stopPropagation(); handleEditClick(session); }}
                            className="p-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          title="Cancel session"
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirmSession(session); }}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Collapsed cancelled sessions */}
              {filteredSessions.filter((s: any) => s.status === 'cancelled').length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowCancelled(c => !c)}
                    className="w-full px-4 py-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showCancelled ? 'rotate-90' : ''}`} />
                    <span className="font-medium">{t('managerGuide.plan.cancelled')}</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                      {filteredSessions.filter((s: any) => s.status === 'cancelled').length}
                    </span>
                  </button>
                  {showCancelled && filteredSessions.filter((s: any) => s.status === 'cancelled').map((session: any) => (
                    <div key={session.session_number} onClick={() => setSelectedSession(session)} className="px-4 py-2.5 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer opacity-60">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(session.status)}`}>
                        {t(`status.${session.status}`) || session.status}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 font-mono">{session.session_number}</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate line-through">{session.product_name}</span>
                          {session.product_chinese_name && <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{session.product_chinese_name}</span>}
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 shrink-0">{session.total_boxes ?? 0} cs</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 mt-auto">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {filteredSessions.length} {filteredSessions.length === 1 ? 'session' : 'sessions'}
            </span>
            <button onClick={onNext} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors">
              {t('managerGuide.next')} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        )}
      </div>

      {(showAddSession || !!editingSession) && (
        <AddSessionModal
          isOpen={showAddSession || !!editingSession}
          onClose={() => { setShowAddSession(false); setEditingSession(null); setAddSessionDate(undefined); }}
          onSuccess={() => { setShowAddSession(false); setEditingSession(null); setAddSessionDate(undefined); queryClient.invalidateQueries(['productionSessions']); }}
          sessions={sessions}
          editSession={editingSession}
          initialDate={addSessionDate}
        />
      )}

      {selectedSession && (
        <SessionDetailsModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          initialTab={getDefaultSessionTab(user?.role)}
        />
      )}

      {/* Cancel session: shared reason dropdown + password confirmation (WCO-2706) */}
      <CancelSessionModal
        session={deleteConfirmSession}
        onClose={() => setDeleteConfirmSession(null)}
      />
    </>
  );
}

// ── STEP 2: Transfers ─────────────────────────────────────

function TransfersStep({ t, onPrev, onNext, hideKpi, guideDate, setGuideDate }: { t: (key: string) => string; onPrev: () => void; onNext: () => void; hideKpi: boolean; guideDate: string; setGuideDate: SetGuideDate }) {
  const [search, setSearch] = useState('');
  const [showVb6, setShowVb6] = useState(false);
  const [transferRangeMode, setTransferRangeMode] = useState<'day' | 'week'>('day');
  const [transferAnchor, setTransferAnchor] = useState(guideDate);
  const [selectedTransfer, setSelectedTransfer] = useState<string | null>(null);
  const [selectedDraftTransfer, setSelectedDraftTransfer] = useState<string | null>(null);
  const [selectedDailyOutDate, setSelectedDailyOutDate] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('transfer_number');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const queryClient = useQueryClient();

  const prefetchTransfer = useCallback((transferNumber: string, hteStatus?: string, hteSource?: string) => {
    // Skip prefetch for drafts and daily-transfer-out — they don't use ICS detail view
    if (hteStatus === 'draft' || hteSource === 'daily-transfer-out') return;
    queryClient.prefetchQuery(
      ['ics-transfer-detail', transferNumber],
      () => inventoryService.getWarehouseTransfer(transferNumber),
      { staleTime: 60000 },
    );
  }, [queryClient]);

  // WCO-2504: pass show_vb6 to the backend (and key on it) so the "Show VB6 Transfers" toggle
  // refetches. Previously it only re-filtered the already-fetched page, so backend-filtered
  // legacy transfers could never be recovered.
  const { data, isLoading } = useQuery(
    ['recent-transfers-hte', showVb6],
    () => inventoryService.searchIcsTransfers({ search_type: 'hte', limit: 50, show_vb6: showVb6 }),
    { staleTime: 30000 },
  );

  // Compute the visible date range (YYYYMMDD strings) based on anchor + mode
  const transferDateRange = useMemo(() => {
    if (transferRangeMode === 'day') {
      const d = transferAnchor.replace(/-/g, '');
      // Short format: "Tue, 04/14"
      const dt = new Date(transferAnchor + 'T00:00:00');
      const wd = dt.toLocaleDateString('en-US', { weekday: 'short' });
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      return { from: d, to: d, label: `${wd}, ${mm}/${dd}` };
    }
    // Week: Monday–Sunday around the anchor
    const anchor = new Date(transferAnchor + 'T00:00:00');
    const dow = anchor.getDay(); // 0=Sun
    const mon = new Date(anchor);
    mon.setDate(anchor.getDate() - ((dow + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    const fmt = (d: Date) => `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    const fmtShort = (d: Date) => `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
    return { from: fmt(mon), to: fmt(sun), label: `${fmtShort(mon)} - ${fmtShort(sun)}` };
  }, [transferAnchor, transferRangeMode]);

  const navigateTransfer = (dir: -1 | 1) => {
    const days = transferRangeMode === 'day' ? 1 : 7;
    setTransferAnchor(shiftDate(transferAnchor, dir * days));
  };

  // WCO-2432: the search row carries only the single legacy destination, but since WCO-2322 a
  // transfer-back day can ship to multiple destinations (one ICS output per (day, destination)).
  // Load the visible range's active outputs + code→name lookups so the "To" column can show ALL
  // destinations for a transfer-back row, matching the supervisor /transfers page.
  const { data: locationCodes = [] } = useQuery(['ics-location-codes'], () => inventoryService.getLocationCodes(), { staleTime: 300000 });
  const { data: warehouseCodes = [] } = useQuery(['ics-warehouse-codes'], () => inventoryService.getWarehouseCodes(), { staleTime: 300000 });
  const destNameByCode = useMemo(() => {
    const m: Record<string, string> = {};
    for (const loc of locationCodes) if (loc.description) m[loc.location_code] = loc.description;
    for (const wh of warehouseCodes) if (!m[wh.warehouse_code] && wh.description) m[wh.warehouse_code] = wh.description;
    return m;
  }, [locationCodes, warehouseCodes]);
  // Normalize any date shape (YYYYMMDD, YYYY-MM-DD, ISO) to a YYYY-MM-DD key.
  const toDayKey = (v: any): string => {
    if (!v) return '';
    const mm = String(v).match(/^(\d{4})-?(\d{2})-?(\d{2})/);
    return mm ? `${mm[1]}-${mm[2]}-${mm[3]}` : '';
  };
  const { data: rangeOutputs = [] } = useQuery(
    ['manager-guide-outputs', transferDateRange.from, transferDateRange.to],
    () => productionOutputScanService.getOutputsByRange(toDayKey(transferDateRange.from), toDayKey(transferDateRange.to)),
    { staleTime: 30000 },
  );
  // Day (YYYY-MM-DD) → its active outputs (each: number + destination + own ship status).
  const outputsByDate = useMemo(() => {
    const map: Record<string, Array<{ output_number: string; destination_warehouse: string; ship_status: string }>> = {};
    for (const o of rangeOutputs) {
      const k = toDayKey(o.production_date);
      if (!k) continue;
      (map[k] = map[k] || []).push({ output_number: o.output_number, destination_warehouse: o.destination_warehouse, ship_status: o.ship_status });
    }
    return map;
  }, [rangeOutputs]);

  const transfers = data?.transfers || [];
  const filteredTransfers = useMemo(() => {
    let list = transfers;
    if (!showVb6) list = list.filter((row) => row.hte_status || Number(row.transfer_number) >= 900000);
    // Date range filter (transfer_date is YYYYMMDD format).
    // Filter strictly by the transfer's own dates.
    const { from, to } = transferDateRange;
    list = list.filter((row) => {
      const td = row.transfer_date || '';
      const rd = row.request_date || '';
      return (td >= from && td <= to) || (rd >= from && rd <= to);
    });
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((row) =>
        (row.transfer_number || '').toLowerCase().includes(q) ||
        (row.hte_status || '').toLowerCase().includes(q) ||
        (row.from_description || '').toLowerCase().includes(q) ||
        (row.to_description || '').toLowerCase().includes(q) ||
        (row.operator_id || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [transfers, search, showVb6, transferDateRange]);

  const sortedTransfers = useMemo(() => {
    return [...filteredTransfers].sort((a, b) => {
      const av = (a as any)[sortField] ?? '';
      const bv = (b as any)[sortField] ?? '';
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredTransfers, sortField, sortDir]);

  // WCO-2432: surface production days that have OUTPUTS but no daily-out transfer row, so an output-only
  // day still shows (outputs are first-class, not piggy-backed on a transfer row) — mirrors supervisor /transfers.
  const displayRows = useMemo(() => {
    const { from, to } = transferDateRange;
    // Days already represented by a transfer-back row in the current list.
    const transferBackDays = new Set(
      sortedTransfers.filter((r) => r.hte_source === 'daily-transfer-out').map((r) => toDayKey(r.request_date))
    );
    // outputsByDate is fetched by date range only — it can't honor the search box, so only add
    // output-only days in the unfiltered view (otherwise they'd be spurious under an active search).
    const outputOnlyEligible = !search.trim();
    const outputOnlyRows = (outputOnlyEligible ? Object.keys(outputsByDate) : [])
      .filter((day) => (outputsByDate[day]?.length ?? 0) > 0 && !transferBackDays.has(day))
      .map((day) => day.replace(/-/g, '')) // YYYY-MM-DD → YYYYMMDD
      .filter((d) => d >= from && d <= to)
      .sort((a, b) => b.localeCompare(a))
      .map((d) => ({ transfer_number: null, request_date: d, transfer_date: d, hte_source: 'daily-transfer-out', hte_status: 'draft', is_complete: 'N' } as unknown as typeof sortedTransfers[0]));
    return [...outputOnlyRows, ...sortedTransfers];
  }, [sortedTransfers, outputsByDate, search, transferDateRange]);

  const thCls = 'sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 cursor-pointer select-none group whitespace-nowrap';
  const SortIcon = ({ field }: { field: string }) => sortField === field
    ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-0.5 text-indigo-500" /> : <ChevronDown className="w-3 h-3 inline ml-0.5 text-indigo-500" />)
    : <ChevronDown className="w-3 h-3 inline ml-0.5 opacity-0 group-hover:opacity-30" />;

  // Resolve effective status: HTE transfers use hte_status, VB6 transfers use is_complete
  const resolveTransferStatus = (tr: typeof transfers[0]) => {
    if (tr.hte_status) return tr.hte_status;
    return tr.is_complete === 'Y' ? 'complete' : 'pending';
  };

  const stats = useMemo(() => {
    const pending = transfers.filter((tr) => { const s = resolveTransferStatus(tr); return s === 'pending' || s === 'draft'; }).length;
    const completed = transfers.filter((tr) => resolveTransferStatus(tr) === 'complete').length;
    const scanned = transfers.filter((tr) => resolveTransferStatus(tr) === 'scanned').length;
    return { pending, completed, scanned, total: transfers.length };
  }, [transfers]);

  // Format YYYYMMDD → YYYY-MM-DD
  const formatIcsDate = (dateStr?: string) => {
    if (!dateStr || dateStr.length !== 8) return dateStr || '-';
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  };

  // Format m_date (YYYYMMDD) + m_time (HHMMSS) → "4/3 2:15 PM"
  const formatLastEdited = (mDate?: string, mTime?: string) => {
    if (!mDate || mDate.length !== 8) return '-';
    const y = +mDate.slice(0, 4), mo = +mDate.slice(4, 6) - 1, d = +mDate.slice(6, 8);
    const h = mTime?.length === 6 ? +mTime.slice(0, 2) : 0;
    const mi = mTime?.length === 6 ? +mTime.slice(2, 4) : 0;
    const dt = new Date(y, mo, d, h, mi);
    return dt.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getStatusBadge = (status?: string) => {
    const styles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      scanned: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
      complete: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    };
    const s = status || 'draft';
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[s] || styles.draft}`}>{t(`warehouseTransfer.${s}`)}</span>;
  };

  const getSourceBadge = (src?: string) => {
    if (!src || src === 'manual') return null;
    const styles: Record<string, string> = {
      'auto-generated': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'daily-transfer-out': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    };
    const labels: Record<string, string> = {
      'auto-generated': t('dailyTransferOut.autoGenerated'),
      'daily-transfer-out': t('dailyTransferOut.dailyOut'),
    };
    return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${styles[src] || ''}`}>{labels[src] || src}</span>;
  };

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {/* KPI Cards */}
      {!hideKpi && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 shrink-0">
          <KpiCard
            icon={<ArrowLeftRight className="w-4 h-4 text-purple-600 dark:text-purple-400" />}
            label={t('managerGuide.transfers.thisWeek')}
            value={isLoading ? '—' : stats.total}
            bg="bg-purple-50 dark:bg-purple-900/20"
          />
          <KpiCard
            icon={<Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
            label={t('managerGuide.transfers.pending')}
            value={isLoading ? '—' : stats.pending}
            bg={stats.pending > 0 ? 'bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-300 dark:ring-amber-600' : 'bg-amber-50 dark:bg-amber-900/20'}
          />
          <KpiCard
            icon={<Package className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
            label={t('managerGuide.transfers.scanned')}
            value={isLoading ? '—' : stats.scanned}
            bg="bg-blue-50 dark:bg-blue-900/20"
          />
          <KpiCard
            icon={<CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />}
            label={t('managerGuide.transfers.completed')}
            value={isLoading ? '—' : stats.completed}
            bg="bg-green-50 dark:bg-green-900/20"
          />
        </div>
      )}

      {/* Transfers table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 gap-3 shrink-0">
          {/* Left: Today + nav arrows + date label */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setTransferAnchor(getToday())}
              className="px-2.5 py-1 text-xs font-medium rounded-lg border border-indigo-300 dark:border-indigo-600 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
            >
              {t('planning.today')}
            </button>
            <button type="button" onClick={() => navigateTransfer(-1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
              <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            <DatePickerDropdown selected={transferAnchor} onSelect={(d) => setTransferAnchor(d)}>
              {({ onClick }) => (
                <button type="button" onClick={onClick} className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                  <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="text-xs font-semibold text-gray-900 dark:text-white min-w-[80px] text-center">{transferDateRange.label}</span>
                </button>
              )}
            </DatePickerDropdown>
            <button type="button" onClick={() => navigateTransfer(1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
              <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            {/* Day / Week toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 text-[11px] ml-1">
              <button
                onClick={() => setTransferRangeMode('day')}
                className={`px-2 py-0.5 rounded-md transition-colors ${transferRangeMode === 'day' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              >
                {t('planning.oneDay')}
              </button>
              <button
                onClick={() => setTransferRangeMode('week')}
                className={`px-2 py-0.5 rounded-md transition-colors ${transferRangeMode === 'week' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              >
                {t('planning.oneWeekShort')}
              </button>
            </div>
          </div>
          {/* Center: search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search')}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
          </div>
          {/* Right: View All link */}
          <Link to="/transfers" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 border border-indigo-200 dark:border-indigo-700 rounded-lg transition-colors shrink-0">
            <ExternalLink className="w-3 h-3" />
            {t('managerGuide.transfers.viewAll')}
          </Link>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : displayRows.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-gray-400 gap-2">
            <ArrowLeftRight className="w-8 h-8 opacity-50" />
            <p className="text-sm">{t('managerGuide.transfers.noTransfers')}</p>
          </div>
        ) : (
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={thCls} onClick={() => handleSort('transfer_number')}>{t('warehouseTransfer.transferNumber')}<SortIcon field="transfer_number" /></th>
                  <th className={thCls} onClick={() => handleSort('hte_status')}>{t('warehouseTransfer.status')}<SortIcon field="hte_status" /></th>
                  <th className={thCls} onClick={() => handleSort('request_date')}>{t('warehouseTransfer.requestDate')}<SortIcon field="request_date" /></th>
                  <th className={thCls} onClick={() => handleSort('transfer_date')}>{t('warehouseTransfer.transferDate')}<SortIcon field="transfer_date" /></th>
                  <th className={thCls} onClick={() => handleSort('from_description')}>{t('warehouseTransfer.from')}<SortIcon field="from_description" /></th>
                  <th className={thCls} onClick={() => handleSort('to_description')}>{t('warehouseTransfer.to')}<SortIcon field="to_description" /></th>
                  <th className={thCls} onClick={() => handleSort('operator_id')}>{t('warehouseTransfer.operator')}<SortIcon field="operator_id" /></th>
                  <th className={`${thCls} text-center`} onClick={() => handleSort('item_count')}>{t('managerGuide.transfers.items')}<SortIcon field="item_count" /></th>
                  <th className={thCls} onClick={() => handleSort('m_date')}>{t('managerGuide.transfers.lastEdited')}<SortIcon field="m_date" /></th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, idx) => {
                  // WCO-2432: the day's active output(s) for a transfer-back row — each output is its own
                  // (number + status + destination) line, stacked above the transfer line, mirroring
                  // the supervisor /transfers page (WCO-2322 multi-destination transfer-back).
                  const dayOutputs = row.hte_source === 'daily-transfer-out' ? (outputsByDate[toDayKey(row.request_date)] || []) : [];
                  const hasTransfer = !!row.transfer_number;
                  const fromVal = row.from_description || row.warehouse_code_from || row.location_code_from || '-';
                  return (
                  <tr
                    key={row.transfer_number || `day-${toDayKey(row.request_date)}`}
                    className={`cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 hover:bg-blue-50/70 dark:hover:bg-blue-900/20 ${
                      idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-800/50'
                    }`}
                    onMouseEnter={() => prefetchTransfer(row.transfer_number, row.hte_status, row.hte_source)}
                    onClick={() => {
                      if (row.hte_source === 'daily-transfer-out') {
                        // Convert YYYYMMDD → YYYY-MM-DD for DailyTransferOutView
                        const d = row.transfer_date;
                        setSelectedDailyOutDate(d ? `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}` : null);
                      } else if (row.hte_status === 'draft') {
                        setSelectedDraftTransfer(row.transfer_number);
                      } else {
                        setSelectedTransfer(row.transfer_number);
                      }
                    }}
                  >
                    {/* Transfer Number — one line per output (number + Transfer Back tag on the first), then the transfer line */}
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {dayOutputs.map((o, i) => (
                        <div key={o.output_number} className="flex items-center gap-2 h-6 mb-0.5">
                          <span className="font-mono font-medium text-blue-600 dark:text-blue-400">{o.output_number}</span>
                          {i === 0 && getSourceBadge('daily-transfer-out')}
                        </div>
                      ))}
                      {hasTransfer && (
                        <div className="flex items-center gap-2 h-6">
                          <span className="font-mono font-medium text-blue-600 dark:text-blue-400">{row.transfer_number}</span>
                          {Number(row.transfer_number) < 900000 && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300">VB6</span>}
                          {row.hte_source && (row.hte_source !== 'daily-transfer-out' || dayOutputs.length === 0) && getSourceBadge(row.hte_source || undefined)}
                        </div>
                      )}
                    </td>
                    {/* Status — each output shows its OWN ship_status, then the transfer's workflow status */}
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {dayOutputs.map((o) => (
                        <div key={o.output_number} className="flex items-center h-6 mb-0.5">{getStatusBadge(o.ship_status)}</div>
                      ))}
                      {hasTransfer && <div className="flex items-center h-6">{getStatusBadge(resolveTransferStatus(row))}</div>}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatIcsDate(row.request_date)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatIcsDate(row.transfer_date)}</td>
                    {/* From — stacked to align with the numbers; outputs + transfer all ship from the same source */}
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">
                      {dayOutputs.map((o) => <div key={o.output_number} className="flex items-center h-6 mb-0.5">{fromVal}</div>)}
                      {hasTransfer && <div className="flex items-center h-6">{fromVal}</div>}
                    </td>
                    {/* To — one destination per output, then the transfer's own destination */}
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">
                      {dayOutputs.map((o) => (
                        <div key={o.output_number} className="flex items-center h-6 mb-0.5">{destNameByCode[o.destination_warehouse] || o.destination_warehouse || '-'}</div>
                      ))}
                      {hasTransfer && (
                        <div className="flex items-center h-6">{row.to_description || row.warehouse_code_to || row.location_code_to || '-'}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.operator_id || '-'}</td>
                    <td className="px-4 py-2.5 text-center text-gray-700 dark:text-gray-300">{row.item_count ?? '-'}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{formatLastEdited(row.m_date, row.m_time)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer with nav buttons */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 mt-auto shrink-0">
          <button onClick={onPrev} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t('managerGuide.previous')}
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 dark:text-gray-400">{displayRows.length} {displayRows.length === 1 ? 'transfer' : 'transfers'}</span>
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
              <input type="checkbox" checked={showVb6} onChange={(e) => setShowVb6(e.target.checked)} className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5" />
              {t('managerGuide.transfers.showVb6')}
            </label>
          </div>
          <button onClick={onNext} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors">
            {t('managerGuide.next')} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Transfer detail modal (non-draft — ICS view) */}
      {selectedTransfer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedTransfer(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-y-auto m-4 p-5" onClick={(e) => e.stopPropagation()}>
            <IcsTransferDetailView
              transferNumber={selectedTransfer}
              onBack={() => setSelectedTransfer(null)}
              hideActions
            />
          </div>
        </div>
      )}

      {/* Draft transfer edit modal */}
      {selectedDraftTransfer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedDraftTransfer(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-y-auto m-4 p-5" onClick={(e) => e.stopPropagation()}>
            <CreateTransferView
              editTransferNumber={selectedDraftTransfer}
              onBack={() => setSelectedDraftTransfer(null)}
              onCreated={() => {
                setSelectedDraftTransfer(null);
                queryClient.invalidateQueries(['recent-transfers-hte']);
              }}
            />
          </div>
        </div>
      )}

      {/* Daily Transfer Out modal */}
      {selectedDailyOutDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedDailyOutDate(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-y-auto m-4 p-5" onClick={(e) => e.stopPropagation()}>
            <DailyTransferOutView
              initialDate={selectedDailyOutDate}
              onBack={() => setSelectedDailyOutDate(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── STEP 3: Produce ───────────────────────────────────────

function ProduceStep({ t, onPrev, onNext, hideKpi, guideDate, setGuideDate }: { t: (key: string) => string; onPrev: () => void; onNext: () => void; hideKpi: boolean; guideDate: string; setGuideDate: SetGuideDate }) {
  const queryClient = useQueryClient();
  const td = guideDate;
  const { data: items = [], isLoading, isFetching } = useQuery(
    ['production-progress', td],
    () => productionService.getDailyWorkQueue(td),
    { staleTime: 15000, refetchInterval: 30000 },
  );

  const kpis = useMemo(() => {
    const activeStations = new Set(items.filter((i) => i.status === 'active' || i.status === 'paused').map((i) => i.station_id));
    const totalBoxes = items.reduce((sum, i) => sum + i.boxes_produced, 0);
    const totalExpectedBoxes = items.reduce((sum, i) => sum + (i.expected_boxes ?? 0), 0);
    const completionPct = totalExpectedBoxes > 0 ? ((totalBoxes / totalExpectedBoxes) * 100).toFixed(2) : 0;
    return { activeStations: activeStations.size, totalBoxes, totalExpectedBoxes, completionPct };
  }, [items]);

  return (
    <>
      {/* KPI Cards */}
      {!hideKpi && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard icon={<Activity className="w-4 h-4 text-green-600 dark:text-green-400" />} label={t('managerGuide.produce.activeStations')} value={kpis.activeStations} bg="bg-green-50 dark:bg-green-900/20" />
          <KpiCard icon={<Package className="w-4 h-4 text-blue-600 dark:text-blue-400" />} label={t('managerGuide.produce.expectedOutput')} value={`${kpis.totalExpectedBoxes} CS`} bg="bg-blue-50 dark:bg-blue-900/20" />
          <KpiCard icon={<TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400" />} label={t('managerGuide.produce.actualOutput')} value={`${kpis.totalBoxes} CS`} bg="bg-purple-50 dark:bg-purple-900/20" />
          <KpiCard icon={<CheckCircle2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />} label={t('manager.completion')} value={kpis.totalExpectedBoxes > 0 ? `${kpis.completionPct}%` : '—'} bg="bg-amber-50 dark:bg-amber-900/20" />
        </div>
      )}

      {/* Live Progress card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col flex-1">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-green-600 dark:text-green-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('managerGuide.produce.liveProgress')}</h3>
            <DateNav guideDate={guideDate} setGuideDate={setGuideDate} t={t} />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => queryClient.invalidateQueries(['production-progress', td])} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400">
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            <Link to="/production-progress" className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline">
              <ExternalLink className="w-3 h-3" />{t('managerGuide.produce.fullView')}
            </Link>
          </div>
        </div>

        {/* Production progress table with filters + sorting */}
        <div className="overflow-auto max-h-[calc(100vh-24rem)] px-4 py-3 space-y-3">
          <ProductionProgressTable items={items} isLoading={isLoading} embedded />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 mt-auto">
          <button onClick={onPrev} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t('managerGuide.previous')}
          </button>
          <button onClick={onNext} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors">
            {t('managerGuide.next')} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

    </>
  );
}

// ── STEP 4: Review ────────────────────────────────────────

const WASTE_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  disposable: { label: 'Disposable', color: 'text-red-600 dark:text-red-400' },
  human_error: { label: 'Human Error', color: 'text-orange-600 dark:text-orange-400' },
  water_liquid: { label: 'Water/Liquid', color: 'text-cyan-600 dark:text-cyan-400' },
  by_product: { label: 'By-Product', color: 'text-blue-600 dark:text-blue-400' },
  by_product_marketable: { label: 'Byproduct - Marketable', color: 'text-green-600 dark:text-green-400' },
  by_product_production: { label: 'Byproduct - Production', color: 'text-teal-600 dark:text-teal-400' },
  remainder_intact: { label: 'Remainder (Intact)', color: 'text-purple-600 dark:text-purple-400' },
  remainder_not_intact: { label: 'Remainder (Not Intact)', color: 'text-pink-600 dark:text-pink-400' },
};

function ReviewStep({ t, onPrev, onComplete, hideKpi, guideDate, setGuideDate }: { t: (key: string) => string; onPrev: () => void; onComplete: () => void; hideKpi: boolean; guideDate: string; setGuideDate: SetGuideDate }) {
  const approvalQueueRef = useRef<ManagerApprovalQueueHandle>(null);
  const td = guideDate;

  const { data: pendingApprovals = [] } = useQuery<PendingApprovalLot[]>(
    ['pending-approvals'], () => inventoryService.getPendingApprovals(), { staleTime: 30000 },
  );

  const { data: eodData, isLoading: eodLoading } = useQuery(
    ['end-of-day-summary', td],
    () => productionService.getEndOfDaySummary(td),
    { staleTime: 60000, keepPreviousData: true },
  );

  const summary = eodData?.summary;
  const tracking = eodData?.material_tracking;
  const products = eodData?.by_product || [];
  const wasteBreakdown = eodData?.waste_breakdown || [];
  const alerts = eodData?.alerts || [];

  const completionPct = summary
    ? ((summary.completed_sessions / Math.max(summary.total_sessions, 1)) * 100).toFixed(2)
    : 0;

  return (
    <>
      {/* Date Navigator */}
      <div className="flex items-center gap-2 px-1">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('managerGuide.steps.review')}</h3>
        <DateNav guideDate={guideDate} setGuideDate={setGuideDate} t={t} />
      </div>

      {/* KPI Cards — merged material flow + summary */}
      {!hideKpi && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          <KpiCard icon={<Package className="w-4 h-4 text-blue-600 dark:text-blue-400" />} label={t('managerGuide.review.input')} value={tracking ? `${tracking.total_initial_material.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs` : '—'} bg="bg-blue-50 dark:bg-blue-900/20" />
          <KpiCard icon={<TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />} label={t('managerGuide.review.output')} value={summary ? `${summary.total_weight_lbs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs` : '—'} sublabel={summary ? `${summary.total_boxes.toLocaleString()} ${t('managerGuide.review.boxes')}` : undefined} bg="bg-emerald-50 dark:bg-emerald-900/20" />
          <KpiCard icon={<Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />} label={t('managerGuide.review.waste')} value={summary ? `${summary.total_waste_lbs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs` : '—'} sublabel={summary && (summary.total_weight_lbs + summary.total_waste_lbs) > 0 ? `${((summary.total_waste_lbs / (summary.total_weight_lbs + summary.total_waste_lbs)) * 100).toFixed(2)}%` : undefined} bg="bg-red-50 dark:bg-red-900/20" />
          <KpiCard icon={<ArrowLeftRight className="w-4 h-4 text-teal-600 dark:text-teal-400" />} label={t('managerGuide.review.returns')} value={tracking ? `${tracking.returns_and_by_products.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs` : '—'} bg="bg-teal-50 dark:bg-teal-900/20" />
          <KpiCard icon={<CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />} label={t('managerGuide.review.sessionsCompleted')} value={summary ? `${summary.completed_sessions} / ${summary.total_sessions}` : '—'} sublabel={summary && summary.total_sessions > 0 ? `${completionPct}%` : undefined} bg="bg-green-50 dark:bg-green-900/20" />
          <KpiCard icon={<AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />} label={t('managerGuide.review.pendingApprovals')} value={pendingApprovals.length} bg={pendingApprovals.length > 0 ? 'bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-300 dark:ring-amber-600' : 'bg-amber-50 dark:bg-amber-900/20'} />
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, idx) => (
            <div key={idx} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${
              alert.severity === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              : alert.severity === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
              : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
            }`}>
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{alert.message}</span>
            </div>
          ))}
        </div>
      )}


      {/* Utilization bar */}
      {tracking && tracking.total_initial_material > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('managerGuide.review.utilization')}</span>
            <span className="text-xs font-bold text-gray-900 dark:text-white">{tracking.material_utilization.toFixed(2)}%</span>
          </div>
          <div className="w-full h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
            <div className="bg-green-500 h-full" style={{ width: `${Math.min((tracking.total_final_product / tracking.total_initial_material) * 100, 100)}%` }} />
            <div className="bg-red-500 h-full" style={{ width: `${Math.min((tracking.actual_waste / tracking.total_initial_material) * 100, 100)}%` }} />
            <div className="bg-teal-500 h-full" style={{ width: `${Math.min((tracking.returns_and_by_products / tracking.total_initial_material) * 100, 100)}%` }} />
          </div>
          <div className="flex flex-wrap gap-3 mt-1.5 text-[10px] text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-500" />{t('managerGuide.review.output')}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500" />{t('managerGuide.review.waste')}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-teal-500" />{t('managerGuide.review.returns')}</span>
          </div>
        </div>
      )}

      {/* Two-column: By Product + Waste Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 flex-1">
        <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('managerGuide.review.byProduct')}</h3>
            <Link to="/supervisor/reports?tab=end-of-day" className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />{t('managerGuide.review.viewFullReport')}
            </Link>
          </div>
          {eodLoading ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-4 h-4 animate-spin text-gray-400" /></div>
          ) : products.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400">{t('managerGuide.review.noData')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">{t('managerGuide.review.product')}</th>
                    <th className="px-3 py-2 text-right text-gray-500 dark:text-gray-400 font-medium">{t('managerGuide.review.sessions')}</th>
                    <th className="px-3 py-2 text-right text-gray-500 dark:text-gray-400 font-medium">{t('managerGuide.review.boxes')}</th>
                    <th className="px-3 py-2 text-right text-gray-500 dark:text-gray-400 font-medium">{t('managerGuide.review.weight')}</th>
                    <th className="px-3 py-2 text-right text-gray-500 dark:text-gray-400 font-medium">{t('managerGuide.review.yield')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {products.slice(0, 8).map((p) => (
                    <tr key={p.product_sku} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900 dark:text-white">{p.product_name}</div>
                        {p.product_chinese_name && <div className="text-[10px] text-gray-500 dark:text-gray-400">{p.product_chinese_name}</div>}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{p.sessions_count}</td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{p.boxes_packed.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{p.total_weight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb</td>
                      <td className={`px-3 py-2 text-right font-medium ${p.yield_percentage >= 0.85 ? 'text-green-600 dark:text-green-400' : p.yield_percentage >= 0.80 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                        {(p.yield_percentage * 100).toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {products.length > 8 && (
                <div className="px-3 py-2 text-center">
                  <Link to="/supervisor/reports?tab=end-of-day" className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline">
                    +{products.length - 8} more...
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('managerGuide.review.wasteBreakdown')}</h3>
            <span className="text-xs font-bold text-red-600 dark:text-red-400">
              {summary ? `${summary.total_waste_lbs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb` : '—'}
            </span>
          </div>
          {eodLoading ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-4 h-4 animate-spin text-gray-400" /></div>
          ) : wasteBreakdown.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400">{t('managerGuide.review.noData')}</div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {[...wasteBreakdown]
                .sort((a, b) => b.total_weight - a.total_weight)
                .map((w) => {
                  const config = WASTE_TYPE_LABELS[w.waste_type] || { label: w.waste_type, color: 'text-gray-600 dark:text-gray-400' };
                  const pct = summary && summary.total_waste_lbs > 0 ? (w.total_weight / summary.total_waste_lbs) * 100 : 0;
                  return (
                    <div key={w.waste_type} className="flex items-center justify-between px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">({w.entry_count})</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div className="bg-red-400 h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white w-20 text-right">{w.total_weight.toFixed(2)} lb</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Approval queue */}
      {pendingApprovals.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('managerGuide.review.approvalsTitle')}</h3>
              <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 rounded-full">{pendingApprovals.length}</span>
            </div>
            <button onClick={() => approvalQueueRef.current?.refresh()} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <ManagerApprovalQueue ref={approvalQueueRef} />
        </div>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { to: '/supervisor/reports?tab=end-of-day', icon: <ClipboardList className="w-5 h-5 text-indigo-500" />, title: t('managerGuide.review.endOfDay'), desc: t('managerGuide.review.endOfDayDesc') },
          { to: '/production-records', icon: <Package className="w-5 h-5 text-blue-500" />, title: t('managerGuide.review.records'), desc: t('managerGuide.review.recordsDesc') },
          { to: '/transfers', icon: <TrendingUp className="w-5 h-5 text-purple-500" />, title: t('managerGuide.review.transfers'), desc: t('managerGuide.review.transfersDesc') },
        ].map((link) => (
          <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            {link.icon}
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">{link.title}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{link.desc}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
          </Link>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <button onClick={onPrev} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t('managerGuide.previous')}
        </button>
        <button onClick={onComplete} className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 rounded-lg transition-colors shadow-sm">
          <CheckCircle2 className="w-4 h-4" /> {t('managerGuide.review.complete')}
        </button>
      </div>
    </>
  );
}

export default ManagerGuide;
